import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';
import { format } from 'date-fns';
import { toast } from 'sonner';

import type { Book } from '@/api/books';
import * as adminApi from '@/api/admin';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/lib/utils';

import { useAuth } from '@/context/AuthContext';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { CARD_WIDE_QUERY, CONTENT_COLUMN, listColumn } from '@/shared/layout/breakpoints';
import ConversionBackdrop from '@/features/catalogue/ConversionBackdrop';
import BookPagination from '@/features/catalogue/BookPagination';
import EditBookDialog from '@/features/catalogue/EditBookDialog';
import RescanPreviewDialog from '@/features/catalogue/RescanPreviewDialog';
import BookPreviewDialog from '@/features/catalogue/BookPreviewDialog';
import SkeletonCard from '@/features/catalogue/SkeletonCard';
import { useBooksQuery } from '@/features/catalogue/hooks/useBooksQuery';
import useScopeName from '@/features/catalogue/hooks/useScopeName';
import { useFavouriteToggle } from '@/features/catalogue/hooks/useFavouriteToggle';
import { useBookDownloads } from '@/features/catalogue/hooks/useBookDownloads';
import BookCard from '@/features/catalogue/BookCard';
import KindleSetupDialog from '@/features/kindle/KindleSetupDialog';
import KindleSendConfirm from '@/features/kindle/KindleSendConfirm';
import { useSendToKindle } from '@/features/kindle/useSendToKindle';
import TelegramSendConfirm from '@/features/send/TelegramSendConfirm';
import { useSendToTelegram } from '@/features/send/useSendToTelegram';
import { useProfileOpener } from '@/features/profile/ProfileOpenerContext';
import { ALL_LANGUAGES } from '@/shared/lib/languageUtils';

const SKELETON_COUNT = 10;

const BooksList: React.FC = () => {
    const { user } = useAuth();
    const { page } = useParams<{ page: string }>();
    const { t } = useTranslation();
    const location = useLocation();
    const { state, dispatch, loadBooks } = useBooksQuery();
    // The search panel needs to say which genre or series it is confined to,
    // and only the loaded page knows the name.
    useScopeName(state.books);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [bookToEdit, setBookToEdit] = useState<Book | null>(null);
    // The book being read, not a boolean: the dialog needs the id and the
    // language together, and holding the book itself keeps them from drifting
    // apart when the reader opens a second book without closing the first.
    const [bookToRead, setBookToRead] = useState<Book | null>(null);

    /**
     * The control that opened the book, so the keyboard can be handed back to
     * it. Measured in Chrome: closing the dialog left focus on <body>, which
     * puts a keyboard reader at the top of the catalogue, several dozen tabs
     * from the book they were just reading. The dialog library restores focus
     * to whatever held it when the content mounted, and here that restore did
     * not happen — rather than work out why someone else's effect declines,
     * the page remembers its own opener and gives the focus back itself.
     */
    const readerOpener = useRef<HTMLElement | null>(null);

    const openBook = (book: Book) => {
        readerOpener.current = document.activeElement as HTMLElement | null;
        setBookToRead(book);
    };

    const closeBook = () => setBookToRead(null);

    // Focus is handed back after the commit, not inside the close handler.
    // The dialog's own focus machinery runs while unmounting its content and
    // lands on <body>; a focus() called before that is simply overwritten,
    // which is what the first version of this did — the test failed exactly
    // as it had before the fix.
    useEffect(() => {
        if (bookToRead !== null) return;
        const opener = readerOpener.current;
        readerOpener.current = null;
        // Only if it is still on the page: a list that re-rendered under the
        // dialog may have replaced the card entirely.
        if (opener && document.contains(opener)) opener.focus();
    }, [bookToRead]);
    const [rescanDialogOpen, setRescanDialogOpen] = useState(false);
    const [bookToRescan, setBookToRescan] = useState<number | null>(null);
    const [downloadError, setDownloadError] = useState<{ title: string; message: string } | null>(
        null,
    );

    // Asked once for the whole list rather than once per card: ten cards would
    // otherwise each hold their own media query listener for the same answer.
    // One boundary for the card, shared with its `sm:` classes — see
    // shared/layout/breakpoints for why this one decision stays in JS.
    const isWideCard = useMediaQuery(CARD_WIDE_QUERY);

    // With a language filter on, every book in the list is in it and saying so
    // on each card is noise. Reading the whole library is when it starts to
    // matter which book is in which language.
    const mixedLanguages = !user?.books_lang || user.books_lang === ALL_LANGUAGES;

    const showDownloadError = (status: number, fallbackMessage?: string) => {
        const byStatus: Record<number, string> = {
            0: t('downloadErrorNetwork'),
            401: t('downloadErrorUnauthorized'),
            403: t('downloadErrorForbidden'),
            404: t('downloadErrorNotFound'),
        };
        setDownloadError({
            title: t('downloadErrorTitle'),
            message: byStatus[status] ?? fallbackMessage ?? t('downloadErrorUnknown'),
        });
    };

    const { handleDownload, handleEpubDownloadClick, handleMobiDownloadClick, isBookConverting } =
        useBookDownloads(showDownloadError);

    const formatDate = (dateString: string) => {
        if (dateString === '') {
            return t('unknownAddDate');
        }
        const timestamp = Date.parse(dateString);
        if (isNaN(timestamp)) {
            return dateString;
        }
        return format(new Date(timestamp), 'dd.MM.yyyy, HH:mm');
    };

    const notify = (message: string) => toast(message, { duration: 2000 });

    // One Kindle sender, one question before sending and one setup dialog for
    // the whole page, not one per card; and the same for Telegram.
    const kindle = useSendToKindle();
    const showTelegram = user?.show_telegram_button !== false;
    const telegram = useSendToTelegram(showTelegram);
    const { openProfile } = useProfileOpener();

    const handleFavBook = useFavouriteToggle(dispatch, notify);

    const handleUpdateBook = async (book: Book) => {
        const newBook = { ...book, approved: !book.approved };
        try {
            // Show the new state at once and put it back if the server disagrees:
            // approving is a frequent action and waiting on a round trip for each
            // one makes moderating a page of books feel broken.
            dispatch({ type: 'UPDATE_BOOK', payload: newBook });
            await adminApi.updateBook(newBook);
        } catch (error) {
            console.error('Error updating book', error);
            dispatch({ type: 'UPDATE_BOOK', payload: book });
        }
    };

    const handleEditBook = (book: Book) => {
        setBookToEdit(book);
        setEditDialogOpen(true);
    };

    const handleEditDialogClose = () => {
        setEditDialogOpen(false);
        setBookToEdit(null);
    };

    const handleBookUpdated = (updatedBook: Book) => {
        dispatch({ type: 'UPDATE_BOOK', payload: updatedBook });
        notify(t('bookUpdatedSuccessfully'));
        loadBooks();
    };

    const handleRescanBook = (bookId: number) => {
        setBookToRescan(bookId);
        setRescanDialogOpen(true);
    };

    const handleRescanDialogClose = () => {
        setRescanDialogOpen(false);
        setBookToRescan(null);
    };

    const handleRescanCompleted = () => {
        notify(t('rescanCompleted'));
        loadBooks();
    };

    return (
        <div className="flex min-h-[calc(100vh-200px)] flex-col">
            {state.loading ? (
                <div className={CONTENT_COLUMN}>
                    {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                        <div key={index} className="py-1.5">
                            <SkeletonCard />
                        </div>
                    ))}
                </div>
            ) : state.books.length === 0 ? (
                <div className={cn(CONTENT_COLUMN, 'py-1.5')}>
                    <div className="rounded border border-border bg-card p-8">
                        <p className="text-center text-lg">{t('noBooksFound')}</p>
                    </div>
                </div>
            ) : (
                /*
                  One column element, holding the cards and the pager together.
                  The pager fills its parent and fits its window to what that
                  row measures, so the two have to be inside the same box — a
                  pager beside the column measures something wider than the
                  cards and lines its arrows up with nothing.
                */
                <div className={cn(CONTENT_COLUMN, 'flex flex-1 flex-col')} {...listColumn}>
                    {state.books.map((book) => (
                        <div key={book.id} className="py-1.5">
                            <BookCard
                                book={book}
                                isWide={isWideCard}
                                showLanguage={mixedLanguages}
                                isSuperuser={Boolean(user?.is_superuser)}
                                formatDate={formatDate}
                                isBookConverting={isBookConverting}
                                onDownload={handleDownload}
                                onPreview={openBook}
                                onEpubRequest={handleEpubDownloadClick}
                                onMobiRequest={handleMobiDownloadClick}
                                onToggleFavourite={handleFavBook}
                                onToggleApproved={handleUpdateBook}
                                onRescan={handleRescanBook}
                                onEdit={handleEditBook}
                                showKindleButton={user?.show_kindle_button !== false}
                                showTelegramButton={showTelegram}
                                kindleConfigured={kindle.configured}
                                isSendingToKindle={kindle.isSending(book.id)}
                                onSendToKindle={kindle.request}
                                telegramConnected={telegram.connected}
                                isSendingToTelegram={telegram.isSending(book.id)}
                                onSendToTelegram={telegram.request}
                            />
                        </div>
                    ))}
                    <div className="mt-auto pt-4">
                        <BookPagination
                            totalPages={state.totalPages}
                            currentPage={parseInt(page as string)}
                            baseUrl={location.pathname + location.search}
                        />
                    </div>
                </div>
            )}

            <KindleSendConfirm
                open={kindle.confirmOpen}
                book={kindle.pending}
                address={kindle.address}
                onConfirm={kindle.confirm}
                onCancel={kindle.cancel}
            />

            <TelegramSendConfirm
                open={telegram.confirmOpen}
                book={telegram.pending}
                botUsername={telegram.botUsername}
                format={telegram.format}
                onConfirm={telegram.confirm}
                onCancel={telegram.cancel}
            />

            <KindleSetupDialog
                open={kindle.setupOpen}
                onClose={kindle.closeSetup}
                onOpenSettings={() => openProfile('kindle')}
            />

            <EditBookDialog
                open={editDialogOpen}
                onClose={handleEditDialogClose}
                book={bookToEdit}
                onBookUpdated={handleBookUpdated}
            />
            <BookPreviewDialog
                open={bookToRead !== null}
                bookId={bookToRead?.id ?? null}
                bookLang={bookToRead?.lang}
                onClose={closeBook}
            />
            <RescanPreviewDialog
                open={rescanDialogOpen}
                onClose={handleRescanDialogClose}
                bookId={bookToRescan}
                onRescanCompleted={handleRescanCompleted}
            />

            <Dialog
                open={downloadError !== null}
                onOpenChange={(open) => !open && setDownloadError(null)}
            >
                <DialogContent className="sm:max-w-sm" closeLabel={t('close')}>
                    <DialogHeader>
                        <DialogTitle>{downloadError?.title ?? t('downloadErrorTitle')}</DialogTitle>
                        <DialogDescription>{downloadError?.message}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button onClick={() => setDownloadError(null)}>OK</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ConversionBackdrop />
        </div>
    );
};

export default BooksList;
